import React, { useState } from 'react';
import API_CONFIG from '../config/api';
import { useAuth } from '../hooks/useAuth';

const PhotoPopup = ({ photo, onClose, isOpen }) => {
  const { makeAuthenticatedRequest } = useAuth();
  const [isFullSize, setIsFullSize] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen || !photo) return null;

  const handleImageLoad = () => {
    setLoading(false);
  };

  const handleImageError = () => {
    setLoading(false);
    setError('Failed to load image');
  };

  const toggleFullSize = () => {
    setIsFullSize(!isFullSize);
  };

  const formatCoordinates = (lat, lng) => {
    const formatDMS = (coord, type) => {
      const absolute = Math.abs(coord);
      const degrees = Math.floor(absolute);
      const minutes = Math.floor((absolute - degrees) * 60);
      const seconds = ((absolute - degrees - minutes / 60) * 3600).toFixed(2);
      const direction = type === 'lat' ? (coord >= 0 ? 'N' : 'S') : (coord >= 0 ? 'E' : 'W');
      return `${degrees}°${minutes}'${seconds}"${direction}`;
    };

    return {
      decimal: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      dms: `${formatDMS(lat, 'lat')}, ${formatDMS(lng, 'lng')}`
    };
  };

  const coordinates = photo.geometry ? formatCoordinates(
    photo.geometry.coordinates[1], 
    photo.geometry.coordinates[0]
  ) : null;

  return (
    <div className="photo-popup-overlay" onClick={onClose}>
      <div className="photo-popup-container" onClick={(e) => e.stopPropagation()}>
        <div className="photo-popup-header">
          <h3 className="photo-popup-title">📷 {photo.properties?.filename || 'Photo'}</h3>
          <button 
            className="photo-popup-close" 
            onClick={onClose}
            title="Close"
          >
            ✕
          </button>
        </div>

        <div className="photo-popup-content">
          <div className="photo-container">
            {loading && <div className="photo-loading">Loading image...</div>}
            {error && <div className="photo-error">{error}</div>}
            
            <img
              src={isFullSize ? photo.properties?.full_image_url : photo.properties?.thumbnail_url}
              alt={photo.properties?.filename || 'Photo'}
              className={`photo-image ${isFullSize ? 'full-size' : 'thumbnail'}`}
              onClick={toggleFullSize}
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{ cursor: 'pointer', display: loading ? 'none' : 'block' }}
              title="Click to toggle size"
            />
            
            {!isFullSize && (
              <div className="photo-size-hint">
                Click image to view full size
              </div>
            )}
          </div>

          <div className="photo-metadata">
            {coordinates && (
              <div className="metadata-section">
                <h4>Location</h4>
                <div className="coordinates">
                  <div><strong>Decimal:</strong> {coordinates.decimal}</div>
                  <div><strong>DMS:</strong> {coordinates.dms}</div>
                </div>
              </div>
            )}

            {photo.properties?.timestamp && (
              <div className="metadata-section">
                <h4>Date Taken</h4>
                <div>{new Date(photo.properties.timestamp).toLocaleString()}</div>
              </div>
            )}

            {photo.properties?.camera_info && (
              <div className="metadata-section">
                <h4>Camera Info</h4>
                <div className="camera-details">
                  {photo.properties.camera_info.make && (
                    <div><strong>Camera:</strong> {photo.properties.camera_info.make} {photo.properties.camera_info.model}</div>
                  )}
                  {photo.properties.camera_info.focal_length && (
                    <div><strong>Focal Length:</strong> {photo.properties.camera_info.focal_length}mm</div>
                  )}
                  {photo.properties.camera_info.aperture && (
                    <div><strong>Aperture:</strong> f/{photo.properties.camera_info.aperture}</div>
                  )}
                  {photo.properties.camera_info.shutter_speed && (
                    <div><strong>Shutter Speed:</strong> {photo.properties.camera_info.shutter_speed}s</div>
                  )}
                  {photo.properties.camera_info.iso && (
                    <div><strong>ISO:</strong> {photo.properties.camera_info.iso}</div>
                  )}
                </div>
              </div>
            )}

            {photo.properties?.file_size && (
              <div className="metadata-section">
                <h4>File Info</h4>
                <div><strong>Size:</strong> {(photo.properties.file_size / 1024 / 1024).toFixed(2)} MB</div>
                {photo.properties.dimensions && (
                  <div><strong>Dimensions:</strong> {photo.properties.dimensions.width} × {photo.properties.dimensions.height}</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .photo-popup-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          padding: 20px;
        }

        .photo-popup-container {
          background: white;
          border-radius: 8px;
          max-width: 90vw;
          max-height: 90vh;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          display: flex;
          flex-direction: column;
        }

        .photo-popup-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px 20px;
          border-bottom: 1px solid #eee;
          background: #f8f9fa;
        }

        .photo-popup-title {
          margin: 0;
          font-size: 18px;
          color: #333;
        }

        .photo-popup-close {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: #666;
          padding: 5px;
          border-radius: 4px;
        }

        .photo-popup-close:hover {
          background: #e9ecef;
          color: #333;
        }

        .photo-popup-content {
          display: flex;
          overflow: hidden;
          flex: 1;
        }

        .photo-container {
          flex: 2;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          background: #f8f9fa;
          position: relative;
          min-height: 300px;
        }

        .photo-image.thumbnail {
          max-width: 100%;
          max-height: 400px;
          object-fit: contain;
          border-radius: 4px;
        }

        .photo-image.full-size {
          max-width: 80vw;
          max-height: 80vh;
          object-fit: contain;
          border-radius: 4px;
        }

        .photo-size-hint {
          position: absolute;
          bottom: 10px;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(0, 0, 0, 0.7);
          color: white;
          padding: 5px 10px;
          border-radius: 4px;
          font-size: 12px;
          opacity: 0.8;
        }

        .photo-loading, .photo-error {
          color: #666;
          font-style: italic;
        }

        .photo-metadata {
          flex: 1;
          padding: 20px;
          overflow-y: auto;
          background: white;
          min-width: 300px;
          max-width: 400px;
        }

        .metadata-section {
          margin-bottom: 20px;
        }

        .metadata-section h4 {
          margin: 0 0 8px 0;
          color: #333;
          font-size: 14px;
          font-weight: 600;
          border-bottom: 1px solid #eee;
          padding-bottom: 4px;
        }

        .metadata-section > div {
          font-size: 13px;
          color: #555;
          margin-bottom: 4px;
        }

        .coordinates div {
          font-family: 'Courier New', monospace;
          background: #f8f9fa;
          padding: 4px 8px;
          border-radius: 3px;
          margin-bottom: 4px;
        }

        .camera-details div {
          margin-bottom: 3px;
        }

        @media (max-width: 768px) {
          .photo-popup-content {
            flex-direction: column;
          }
          
          .photo-metadata {
            min-width: unset;
            max-width: unset;
          }

          .photo-container {
            min-height: 200px;
          }
        }
      `}</style>
    </div>
  );
};

export default PhotoPopup;